import { CorePhases } from './types';
import { modes, CustomEvents } from '@powerplay/common';
import { PhaseIntroManager, PhaseStartListManager, PhaseProvisionalResultsManager, PhaseFinalResultsManager, PhaseDisciplineManager, PhaseInstructionsManager } from './phases';
import { PhaseIntro2Manager } from './phases/PhaseIntro2Manager';
export class CorePhasesManager {
    cameraManager;
    inputsManager;
    fpsManager;
    settings;
    gameStats;
    trainingManager;
    requestManager;
    activePhase = 0;
    phases = [];
    phasesCount = 0;
    phasesChain = [];
    disciplineActualAttempt = 0;
    firstInstructions = false;
    disciplineAttemptsCount = 1;
    provisionalResultsAllowed = true;
    provisionalResultsFrequency = 1;
    showIntro = true;
    resetActualPhase = false;
    constructor(cameraManager, inputsManager, fpsManager, settings, gameStats, trainingManager, requestManager) {
        this.cameraManager = cameraManager;
        this.inputsManager = inputsManager;
        this.fpsManager = fpsManager;
        this.settings = settings;
        this.gameStats = gameStats;
        this.trainingManager = trainingManager;
        this.requestManager = requestManager;
    }
    init(attemptsCount = 1, firstInstructions) {
        this.disciplineAttemptsCount = attemptsCount;
        this.firstInstructions = firstInstructions;
        this.setPhasesChain();
        this.phasesCount = this.phasesChain.length - 1;
        this.createPhases(firstInstructions);
        window.addEventListener(CustomEvents.finishPhase, this.onFinishPhase);
    }
    setPhasesChain() {
        this.phasesChain = [
            CorePhases.instructions
        ];
        if (this.showIntro) {
            this.phasesChain.push(CorePhases.intro);
            this.phasesChain.push(CorePhases.intro2);
        }
        if (!modes.isTrainingMode()) {
            if (modes.isTournament()) {
                this.phasesChain.push(CorePhases.provisionalResults);
            }
            else {
                this.phasesChain.push(CorePhases.startList);
            }
        }
        for (let i = 0; i < this.disciplineAttemptsCount; i++) {
            if (i > 0 && !modes.isTrainingMode() &&
                this.provisionalResultsAllowed &&
                i % this.provisionalResultsFrequency === 0) {
                this.phasesChain.push(CorePhases.provisionalResults);
            }
            this.phasesChain.push(CorePhases.discipline);
        }
        this.phasesChain.push(CorePhases.finalResults);
    }
    insertNewProvisionalResults() {
        this.phasesChain[this.phasesChain.length - 1] = CorePhases.provisionalResults;
        this.phasesChain.push(CorePhases.discipline);
        this.phasesChain.push(CorePhases.finalResults);
        this.phasesCount += 2;
    }
    insertNewDisciplinePhase() {
        this.phasesChain[this.phasesChain.length - 1] = CorePhases.discipline;
        this.phasesChain.push(CorePhases.finalResults);
        this.phasesCount += 1;
    }
    createPhases = (firstInstructions) => {
        this.phases[CorePhases.instructions] = new PhaseInstructionsManager(firstInstructions, this.cameraManager, this.inputsManager, this.fpsManager, this.requestManager);
        this.phases[CorePhases.intro] = new PhaseIntroManager(this.cameraManager, this.inputsManager, this.fpsManager, this.requestManager);
        this.phases[CorePhases.intro2] = new PhaseIntro2Manager(this.cameraManager, this.inputsManager, this.fpsManager, this.requestManager);
        this.phases[CorePhases.startList] = new PhaseStartListManager(this.cameraManager, this.inputsManager, this.fpsManager, this.requestManager);
        this.phases[CorePhases.discipline] = new PhaseDisciplineManager(this.cameraManager, this.inputsManager, this.fpsManager, this.requestManager);
        this.phases[CorePhases.provisionalResults] = new PhaseProvisionalResultsManager(this.cameraManager, this.inputsManager, this.fpsManager, this.requestManager);
        this.phases[CorePhases.finalResults] = new PhaseFinalResultsManager(this.cameraManager, this.inputsManager, this.fpsManager, this.requestManager, this.settings, this.gameStats, this.trainingManager);
    };
    getActualPhase = () => {
        if (this.activePhase > this.phasesCount)
            return undefined;
        return this.phases[this.phasesChain[this.activePhase]];
    };
    getPhase = (phaseIndex) => {
        return this.phases[phaseIndex];
    };
    isActivePhaseByType = (type) => {
        return this.getActualPhase()?.phaseNumber === type;
    };
    setPhaseDuration = (phase, duration) => {
        this.getPhase(phase)?.setPhaseDuration(duration);
    };
    setPhaseNotSkippableFrames = (phase, notSkippableFrames) => {
        this.getPhase(phase)?.setPhaseNotSkippableFrames(notSkippableFrames);
    };
    startFirstPhase = () => {
        this.getActualPhase()?.startPhase();
    };
    setNextPhase = () => {
        if (this.resetActualPhase) {
            this.resetActualPhase = false;
            this.getActualPhase()?.startPhase();
            return;
        }
        this.activePhase++;
        if (this.activePhase > this.phasesCount) {
            window.dispatchEvent(new CustomEvent(CustomEvents.finalRedirectDone));
            this.requestManager.redirect();
            return;
        }
        if (this.isActivePhaseByType(CorePhases.discipline)) {
            this.disciplineActualAttempt++;
        }
        this.getActualPhase()?.startPhase();
    };
    onFinishPhase = () => {
        this.setNextPhase();
    };
    update = () => {
        this.getActualPhase()?.update();
    };
    setFinalPhase() {
        this.activePhase = this.phasesChain.length - 1;
        this.getActualPhase()?.startPhase();
    }
    endAllPhases(disciplinePhasesFinish) {
        if (this.phasesChain[this.activePhase] === CorePhases.discipline) {
            disciplinePhasesFinish();
        }
        else if (this.phasesChain[this.activePhase] === CorePhases.startList) {
            this.getActualPhase()?.finishPhase();
            this.endAllPhases(disciplinePhasesFinish);
        }
        else if (this.phasesChain[this.activePhase] === CorePhases.intro) {
            this.getActualPhase()?.finishPhase();
            this.endAllPhases(disciplinePhasesFinish);
        }
        else if (this.phasesChain[this.activePhase]) {
            this.phases[this.activePhase].finishPhase();
            this.endAllPhases(disciplinePhasesFinish);
        }
    }
    reset() {
        this.createPhases(false);
        this.activePhase = 0;
        this.disciplineActualAttempt = 0;
        this.requestManager.reset();
        this.gameStats.setDisciplineDataToLog({});
        this.gameStats.setExitedGame(false);
    }
}
//# sourceMappingURL=CorePhasesManager.js.map